import { useMemo, useState } from "react"
import { useQuery } from "react-query"
import { useParams } from "react-router-dom"
import { useTranslation } from "react-i18next"
import { PageView, PageTopBar } from "../../components"
import { Loader } from "../../components/Loader"
import { fetchOperatorCosts } from "../../services/workforceServices"
import { WorkforceTabs } from "./WorkforceTabs"
import { DailyAttendanceChart } from "./components/DailyAttendanceChart"
import { AttendanceBreakdownTable } from "./components/AttendanceBreakdownTable"
import { extractBackendError } from "../../utils/workforceUtils"

const MAX_RANGE_DAYS = 90

const todayIso = () => {
    const d = new Date()
    const y = d.getFullYear()
    const m = String(d.getMonth() + 1).padStart(2, '0')
    const day = String(d.getDate()).padStart(2, '0')
    return `${y}-${m}-${day}`
}

const subtractDaysIso = (iso, days) => {
    const d = new Date(iso + 'T00:00:00')
    d.setDate(d.getDate() - days)
    const y = d.getFullYear()
    const m = String(d.getMonth() + 1).padStart(2, '0')
    const day = String(d.getDate()).padStart(2, '0')
    return `${y}-${m}-${day}`
}

const diffDaysInclusive = (fromIso, toIso) => {
    const a = new Date(fromIso + 'T00:00:00')
    const b = new Date(toIso + 'T00:00:00')
    return Math.round((b - a) / (1000 * 60 * 60 * 24)) + 1
}

const Kpi = ({ label, value, hint }) => (
    <div className="border rounded-lg bg-white p-4">
        <div className="text-xs uppercase text-gray-500">{label}</div>
        <div className="text-2xl font-semibold text-gray-900 mt-1 tabular-nums">{value}</div>
        {hint && <div className="text-[11px] text-gray-400 mt-1">{hint}</div>}
    </div>
)

export const OperatorCostsContainer = () => {
    const { warehouse_id } = useParams()
    const { t } = useTranslation()

    const defaultTo = todayIso()
    const defaultFrom = subtractDaysIso(defaultTo, 29)

    const [from, setFrom] = useState(defaultFrom)
    const [to, setTo] = useState(defaultTo)
    const [applied, setApplied] = useState({ from: defaultFrom, to: defaultTo })
    const [rangeError, setRangeError] = useState('')

    const { data, isLoading, isError, error, isFetching } = useQuery(
        ['workforce', warehouse_id, 'costs-range', applied],
        () => fetchOperatorCosts(warehouse_id, { from: applied.from, to: applied.to }),
        { keepPreviousData: true }
    )

    const handleApply = () => {
        setRangeError('')
        if (!from || !to) return
        if (from > to) {
            setRangeError(t('workforce.analytics.errors.from_before_to'))
            return
        }
        if (diffDaysInclusive(from, to) > MAX_RANGE_DAYS) {
            setRangeError(t('workforce.analytics.errors.max_range', { max: MAX_RANGE_DAYS }))
            return
        }
        setApplied({ from, to })
    }

    const view = useMemo(() => {
        if (!data) return null
        const rawDays = Array.isArray(data.days) ? data.days : []

        // Per-day aggregates split by shift_period + primary/extra
        const days = rawDays.map((d) => {
            const groups = Array.isArray(d.groups) ? d.groups : []
            let day_primary = 0
            let night_primary = 0
            let unknown_primary = 0
            let extras_ops = 0
            let extras_hours = 0
            for (const g of groups) {
                if (g.type === 'extra') {
                    extras_ops += Number(g.operator_count) || 0
                    extras_hours += Number(g.total_extra_hours) || 0
                } else {
                    const present = Number(g.present_count) || 0
                    if (g.shift_period === 'day') day_primary += present
                    else if (g.shift_period === 'night') night_primary += present
                    else unknown_primary += present
                }
            }
            const primary_total = day_primary + night_primary + unknown_primary
            return {
                date: d.date,
                day_primary,
                night_primary,
                unknown_primary,
                primary_total,
                extras_ops,
                extras_hours,
            }
        })

        const total_primary = days.reduce((acc, d) => acc + d.primary_total, 0)
        const total_day = days.reduce((acc, d) => acc + d.day_primary, 0)
        const total_night = days.reduce((acc, d) => acc + d.night_primary, 0)
        const total_extras_ops = days.reduce((acc, d) => acc + d.extras_ops, 0)
        const total_extras_hours = days.reduce((acc, d) => acc + d.extras_hours, 0)
        const active_days = days.filter((d) => d.primary_total + d.extras_ops > 0).length

        // Breakdown by (contract_type, shift_period, primary/extra)
        const breakdownMap = new Map()
        for (const d of rawDays) {
            const groups = Array.isArray(d.groups) ? d.groups : []
            for (const g of groups) {
                const period = g.shift_period || null
                const key = `${g.contract_type || '—'}|${period || '—'}|${g.type}`
                const curr = breakdownMap.get(key) || {
                    contract_type: g.contract_type,
                    shift_period: period,
                    row_type: g.type,
                    operator_types: new Set(),
                    present: 0,
                    operator_count: 0,
                    extra_hours: 0,
                }
                if (g.operator_type) curr.operator_types.add(g.operator_type)
                curr.present += Number(g.present_count) || 0
                curr.operator_count += Number(g.operator_count) || 0
                curr.extra_hours += Number(g.total_extra_hours) || 0
                breakdownMap.set(key, curr)
            }
        }
        const breakdown = Array.from(breakdownMap.values())
            .map((r) => ({ ...r, operator_types: Array.from(r.operator_types).sort() }))
            .sort((a, b) =>
                (a.contract_type || '').localeCompare(b.contract_type || '') ||
                (a.shift_period || '').localeCompare(b.shift_period || '') ||
                (a.row_type === b.row_type ? 0 : a.row_type === 'primary' ? -1 : 1)
            )

        const hasData = total_primary > 0 || total_extras_ops > 0 || breakdown.length > 0
        return {
            days,
            breakdown,
            total_primary,
            total_day,
            total_night,
            total_extras_ops,
            total_extras_hours,
            active_days,
            hasData,
        }
    }, [data])

    const backendMessage = isError ? extractBackendError(error).message : ''

    return (
        <PageView
            topMenu={
                <PageTopBar>
                    <div className="text-lg font-semibold">{t('workforce.analytics.title')}</div>
                </PageTopBar>
            }
            topMenuFullWidth={true}
            childrenFullWidth={true}
        >
            <WorkforceTabs />

            <div className="flex flex-wrap items-end gap-3 mt-4">
                <div>
                    <label className="block text-xs font-medium text-gray-600 mb-1">{t('workforce.analytics.from')}</label>
                    <input type="date" value={from} onChange={(e) => setFrom(e.target.value)}
                        className="border border-gray-300 rounded-md text-sm px-3 py-2" />
                </div>
                <div>
                    <label className="block text-xs font-medium text-gray-600 mb-1">{t('workforce.analytics.to')}</label>
                    <input type="date" value={to} onChange={(e) => setTo(e.target.value)}
                        className="border border-gray-300 rounded-md text-sm px-3 py-2" />
                </div>
                <button type="button" onClick={handleApply}
                    className="h-10 px-4 border border-gray-300 rounded-md text-sm font-medium text-gray-700 bg-white hover:bg-gray-50">
                    {t('workforce.analytics.apply')}
                </button>
                {rangeError && <div className="text-sm text-red-600">{rangeError}</div>}
            </div>

            {isError && !rangeError && (
                <div className="mt-4 rounded-md bg-red-50 border border-red-200 p-3 text-sm text-red-700">
                    {backendMessage || t('workforce.errors.generic')}
                </div>
            )}

            {isLoading ? (
                <div className="mt-6"><Loader show={true} /></div>
            ) : view ? (
                !view.hasData ? (
                    <div className="mt-6 p-8 text-center text-gray-500 border rounded bg-white">
                        {t('workforce.analytics.empty')}
                    </div>
                ) : (
                    <>
                        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mt-4">
                            <Kpi
                                label={t('workforce.analytics.kpi_present')}
                                value={view.total_primary.toLocaleString()}
                                hint={t('workforce.analytics.kpi_present_hint', { day: view.total_day, night: view.total_night })}
                            />
                            <Kpi
                                label={t('workforce.analytics.kpi_active_days')}
                                value={view.active_days.toLocaleString()}
                            />
                            <Kpi
                                label={t('workforce.analytics.kpi_extras_ops')}
                                value={view.total_extras_ops.toLocaleString()}
                            />
                            <Kpi
                                label={t('workforce.analytics.kpi_extras_hours')}
                                value={view.total_extras_hours.toLocaleString(undefined, { maximumFractionDigits: 1 })}
                            />
                        </div>

                        <div className="mt-6">
                            <h2 className="text-base font-semibold text-gray-900 mb-2">{t('workforce.analytics.daily_attendance')}</h2>
                            <div className="bg-white border rounded-lg p-3">
                                <DailyAttendanceChart days={view.days} />
                            </div>
                        </div>

                        <div className="mt-6">
                            <h2 className="text-base font-semibold text-gray-900 mb-2">{t('workforce.analytics.breakdown')}</h2>
                            <AttendanceBreakdownTable rows={view.breakdown} />
                        </div>
                    </>
                )
            ) : null}

            {isFetching && !isLoading && (
                <div className="mt-2 text-xs text-gray-500">{t('workforce.updating')}</div>
            )}
        </PageView>
    )
}
